/**
 * ArticlesLanding — /articles list page with a "Write new article" CTA.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { PenSquare, Clock, Eye, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function ArticlesLanding() {
  const [articles, setArticles] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/articles?status=published&limit=30`);
        if (alive) setArticles(data.articles || []);
      } catch (e) {
        if (alive) setError(e.message);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 lg:px-6 py-8" data-testid="articles-landing">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Long-form</span>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900">Articles &amp; News</h1>
          <p className="text-sm text-slate-600 mt-1">
            Rich long-form stories from the ChatsPulse community. Read, listen, translate.
          </p>
        </div>
        <Link
          to="/articles/new"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 shadow-sm"
          data-testid="articles-new-btn"
        >
          <PenSquare className="w-4 h-4" /> Write an article
        </Link>
      </div>

      <div className="mt-6" data-testid="articles-list">
        {error ? (
          <p className="text-sm text-red-600">Failed to load: {error}</p>
        ) : articles === null ? (
          <p className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
        ) : articles.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-slate-200 rounded-xl">
            <p className="text-sm text-slate-500">No published articles yet.</p>
            <Link to="/articles/new" className="mt-3 inline-block text-sm font-semibold text-blue-700 hover:underline" data-testid="articles-empty-cta">
              Be the first to write one →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {articles.map((a) => (
              <Link
                key={a.id}
                to={`/articles/${a.slug}`}
                className="block rounded-2xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition p-5 bg-white"
                data-testid={`article-card-${a.slug}`}
              >
                {a.cover_image_url && (
                  <img src={a.cover_image_url} alt="" className="w-full aspect-[16/9] rounded-lg object-cover mb-3" />
                )}
                {a.category && (
                  <span className="inline-block text-[10px] uppercase tracking-widest font-bold text-blue-700 mb-1.5">
                    {a.category.replace(/-/g, " ")}
                  </span>
                )}
                <h3 className="text-lg font-bold text-slate-900 leading-tight">{a.title}</h3>
                {a.subtitle && (
                  <p className="text-sm text-slate-600 mt-1 line-clamp-2">{a.subtitle}</p>
                )}
                <div className="mt-3 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                  {a.author?.name && <span>By {a.author.name}</span>}
                  {a.published_at && <span>· {new Date(a.published_at).toLocaleDateString()}</span>}
                  {a.reading_time_min && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {a.reading_time_min}m</span>}
                  {typeof a.view_count === "number" && <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {a.view_count}</span>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
